/* eslint-disable @typescript-eslint/no-misused-promises */

import React, { useRef, useState, useEffect, useMemo } from 'react';
import AddReportFilter from '../../../components/Filter/AddReportFilter';
import ReportPage from '../../../components/ReportPage';
import ReportForm, { IReportFormInstance } from '../ReportForm';
import useDPSReportSession from '../../../_hooks/useDPSReportSession';
import useAddDPSReport from '../../_hooks/useAddDPSReport';
import { CARD_TITLE as COMMENT_CARD_TITLE } from '../Comment';
import { CARD_TITLE as CHANGES_CARD_TITLE } from '../UpdatesOfWeek/utils';
import { CARD_TITLE as MEMO_CARD_TITLE } from '../MemoPendingApproval/utils';
import { CARD_TITLE as CALENDAR_CARD_TITLE } from '../../../components/Calendar/utils';
import { useMemoizedFn, useDeepCompareEffect } from 'ahooks';
import { useAutoSaveParams, EDIT_PAGE_KEY_MAP } from '@gp/hooks/useAutoSaveParams';
import AutoSaveModal from '@gp/components/AutoSaveModal';
import type { IDPSReportBaseVO } from '@gp/types/dpsReport/common';
import type { IDPSReportModuleTitles } from '@gp/types/dpsReport/v3';
import type { IDPSReportFormValue } from '../../types';
import Loading from '@gp/components/Loading';
import '../../../scss/index.scss';

const DEFAULT_FORM_VALUE: IDPSReportFormValue = {
  comment: '',
  project_comment: [],
  changes: [],
  memo: [],
  calendar: {
    start_date: '',
    end_date: '',
    list: [],
  },
};

const getDisabledWeeks = (reports?: IDPSReportBaseVO[]): string[][] => {
  if (!Array.isArray(reports) || !reports.length) return [];
  return reports.map((report) => {
    const { start_date: startDate, end_date: endDate } = report || {};
    return !startDate || !endDate ? [] : [startDate, endDate];
  });
};

const AddReport: React.FC = () => {
  const reports = useDPSReportSession();

  const disabledWeeks = getDisabledWeeks(reports);
  const reportFormInstance = useRef<IReportFormInstance | null>(null);

  const {
    isGuide,
    isLoading,
    isCalendarInnerLoading,
    setIsCalendarInnerLoading,
    isSubmitting,
    submitType,
    reportHelper,
    projects,
    filter,
    onFilterChange,
    onCalendarRefreshData,
    onSubmit,
    reportParams,
  } = useAddDPSReport({
    disabledWeeks,
    getReport: () => ({ ...reportFormValue }),
    setReport: (payload) => {
      setReportFormValue({ ...reportFormValue, ...payload });
    },
    initialize: (payload) => {
      reportFormInstance.current?.initialize(payload);
    },
  });

  const defaultTitles = {
    comment_title: 'Important GR & Check-in Updates',
    changes_title: 'P-team Project Updates',
    memo_title: 'Memo Pending Approval',
    calendar_title: 'Calendar',
  };

  const [titles, setTitles] = useState<IDPSReportModuleTitles>(defaultTitles);

  const handleTitleChange = (key: string, payload: string) => {
    setTitles({ ...titles, [key]: payload });
  };

  const [reportFormValue, setReportFormValue] = useState<IDPSReportFormValue>(DEFAULT_FORM_VALUE);
  const [editAutoSaveData, setEditAutoSaveData] = useState<IDPSReportFormValue>(DEFAULT_FORM_VALUE);
  const [editTitlesData, setEditTitlesData] = useState<IDPSReportModuleTitles>(defaultTitles);
  const [isShowReportPage, setIsShowReportPage] = useState(false);
  const [autoSaveVisible, setAutoSaveVisible] = useState(false);
  const initialParamsRef = useRef<any>(null);

  useDeepCompareEffect(() => {
    if (initialParamsRef.current === null && reportFormValue !== DEFAULT_FORM_VALUE) {
      initialParamsRef.current = {
        title: titles,
        report: reportFormValue,
      };
    }
  }, [reportFormValue, titles]);

  useDeepCompareEffect(() => {
    setEditAutoSaveData(reportFormValue);
  }, [reportFormValue]);
  useDeepCompareEffect(() => {
    setEditTitlesData(titles);
  }, [titles]);

  const pageKeyCur = useMemo(() => {
    return `${EDIT_PAGE_KEY_MAP.pipeline_DPSNewsletter_add}_v3_${filter?.start_date}_${filter?.end_date}`;
  }, [filter]);

  // 自动保存hooks
  const { getSaveParamsValue, showRestorePrompt, deleteSaveParamsByPageKey } = useAutoSaveParams({
    pageKey: pageKeyCur,
  });

  const isShowReportFormCur = useMemo(() => {
    return !!filter?.start_date && !!filter?.end_date;
  }, [filter]);

  useEffect(() => {
    if (isLoading) return;
    if (!isLoading) {
      if (showRestorePrompt(pageKeyCur) && isShowReportFormCur) {
        setAutoSaveVisible(true);
        setIsShowReportPage(false);
      } else {
        setIsShowReportPage(true);
      }
    }
  }, [isLoading, pageKeyCur]);

  const handleAutoSaveConfirm = () => {
    const saveObj = getSaveParamsValue(pageKeyCur) || {};
    setEditAutoSaveData({
      ...reportFormValue,
      ...saveObj.report,
    });
    setEditTitlesData({
      ...titles,
      ...saveObj.title,
    });
    setAutoSaveVisible(false);
    setIsShowReportPage(true);
  };

  const handleAutoSaveCancel = () => {
    deleteSaveParamsByPageKey(pageKeyCur);
    setEditAutoSaveData(reportFormValue);
    setEditTitlesData(titles);
    setAutoSaveVisible(false);
    setIsShowReportPage(true);
  };

  const handleConfirm = useMemoizedFn(async (isPublish: boolean) => {
    deleteSaveParamsByPageKey(pageKeyCur);
    onSubmit(titles, { ...reportFormValue }, isPublish, {
      tips: 'Add Successful! Unpublished by default',
    });
  });

  const onOkHandle = () => {
    deleteSaveParamsByPageKey(pageKeyCur);
  };

  return (
    <>
      {isShowReportPage && !isLoading && (
        <ReportPage
          className="dbg-add-dps-report-page dbg-add-dps-report-page--v3"
          title="Add new Newsletter"
          isEdit={false}
          isGuide={isGuide}
          isShowReportForm={isShowReportFormCur}
          isLoading={isLoading}
          isSubmitting={isSubmitting}
          submitType={submitType}
          reportHelper={reportHelper}
          onConfirm={handleConfirm}
          onOkHandle={onOkHandle}
          topbar={
            <AddReportFilter
              disabledWeeks={disabledWeeks}
              value={filter}
              onChange={onFilterChange}
            />
          }
          form={
            <ReportForm
              ref={reportFormInstance}
              isLoading={isLoading}
              isCalendarInnerLoading={isCalendarInnerLoading}
              setIsCalendarInnerLoading={setIsCalendarInnerLoading}
              reportHelper={reportHelper}
              titles={editTitlesData}
              onTitleChange={handleTitleChange}
              filter={filter}
              projects={projects}
              onCalendarRefreshData={onCalendarRefreshData}
              value={editAutoSaveData}
              onChange={setReportFormValue}
              initialParams={initialParamsRef.current}
              pageKeyCur={pageKeyCur}
              isEdit={false}
              reportParams={reportParams}
              isShowReportPage={isShowReportPage}
            />
          }
        />
      )}
      {isLoading && (
        <div style={{ height: 66, color: '#262832' }}>
          <Loading className="dbg-dps-report__state-action" loading />
        </div>
      )}
      {/* 咨询自动保存弹窗 */}
      <AutoSaveModal
        visible={autoSaveVisible}
        onConfirm={handleAutoSaveConfirm}
        onClose={handleAutoSaveCancel}
      />
    </>
  );
};

export default AddReport;
